import moment from "moment";

export interface SelectedOfferLike {
  offer_type?: string;
  no_of_transactions?: number | string;
}

export interface SelectedOfferLimitError {
  code:
    | "INVALID_TRANSACTION_COUNT"
    | "TRANSACTION_LIMIT_EXCEEDED"
    | "END_DATE_LIMIT_EXCEEDED";
  message: string;
}

export type BudgetCalculatorAssistanceReason =
  | "NEGATIVE_NET_INCOME"
  | "PAYMENT_AMOUNT_OUTSIDE_RANGE"
  | "TRANSACTION_LIMIT_EXCEEDED"
  | "END_DATE_LIMIT_EXCEEDED";

const BUDGET_CALCULATOR_ASSISTANCE_MESSAGES: Record<
  BudgetCalculatorAssistanceReason,
  string
> = {
  NEGATIVE_NET_INCOME:
    "Your monthly expenses are greater than your monthly income. Request assistance below.",
  PAYMENT_AMOUNT_OUTSIDE_RANGE:
    "Your net monthly income is outside the allowed payment amount range. Request assistance below.",
  TRANSACTION_LIMIT_EXCEEDED:
    "Your monthly budget would require more payments than the maximum allowed. Request assistance below.",
  END_DATE_LIMIT_EXCEEDED:
    "Your monthly budget would require a payment plan longer than the allowed period. Request assistance below.",
};

export const getBudgetCalculatorAssistanceMessage = (
  reason: BudgetCalculatorAssistanceReason
): string => BUDGET_CALCULATOR_ASSISTANCE_MESSAGES[reason];

export interface BudgetCalculatorLimitResult {
  requiredTransactions: number | null;
  transactionLimit: number | null;
  endDateTransactionLimit: number | null;
  effectiveTransactionLimit: number | null;
  isExceeded: boolean;
  reason: Extract<
    BudgetCalculatorAssistanceReason,
    "TRANSACTION_LIMIT_EXCEEDED" | "END_DATE_LIMIT_EXCEEDED"
  > | null;
}

interface ParsedEndDateLimit {
  value: number;
  momentUnit: "days" | "weeks" | "months";
}

const getScheduleDate = (transaction: any): string =>
  transaction?.billing_date ||
  transaction?.payment_date ||
  transaction?.date ||
  "";

const parseEndDateLimit = (
  configuredLimit: unknown
): ParsedEndDateLimit | null => {
  const [rawValue, rawUnit] = `${configuredLimit || ""}`.trim().split("-");
  const value = Number(rawValue);
  const unit = rawUnit?.toUpperCase();

  if (!Number.isFinite(value) || value < 0 || !["D", "W", "M"].includes(unit)) {
    return null;
  }

  return {
    value,
    momentUnit: unit === "M" ? "months" : unit === "W" ? "weeks" : "days",
  };
};

const normalizeTransactionLimit = (configuredLimit: unknown): number | null => {
  const transactionLimit = Number(configuredLimit);

  return Number.isFinite(transactionLimit) && transactionLimit >= 1
    ? Math.floor(transactionLimit)
    : null;
};

export const getBudgetCalculatorEndDateTransactionLimit = (
  configuredLimit: unknown,
  now: Date = new Date()
): number | null => {
  const parsedLimit = parseEndDateLimit(configuredLimit);
  const startDate = moment(now).startOf("day");

  if (!parsedLimit || !startDate.isValid()) return null;

  const maximumDate = startDate
    .clone()
    .add(parsedLimit.value, parsedLimit.momentUnit)
    .endOf("day");

  // Budget Calculator schedules are monthly. Count complete monthly periods
  // in the configured window so a 3-M limit permits three installments.
  return Math.max(0, Math.floor(maximumDate.diff(startDate, "months", true)));
};

export const getBudgetCalculatorLimitResult = ({
  balance,
  monthlyAmount,
  configuredTransactionLimit,
  configuredEndDateLimit,
  now = new Date(),
}: {
  balance: unknown;
  monthlyAmount: unknown;
  configuredTransactionLimit: unknown;
  configuredEndDateLimit: unknown;
  now?: Date;
}): BudgetCalculatorLimitResult => {
  const normalizedBalance = Number(balance);
  const normalizedMonthlyAmount = Number(monthlyAmount);
  const transactionLimit = normalizeTransactionLimit(
    configuredTransactionLimit
  );
  const endDateTransactionLimit = getBudgetCalculatorEndDateTransactionLimit(
    configuredEndDateLimit,
    now
  );
  const configuredLimits = [transactionLimit, endDateTransactionLimit].filter(
    (limit): limit is number => limit !== null
  );
  const effectiveTransactionLimit = configuredLimits.length
    ? Math.min(...configuredLimits)
    : null;

  if (
    !Number.isFinite(normalizedBalance) ||
    normalizedBalance <= 0 ||
    !Number.isFinite(normalizedMonthlyAmount) ||
    normalizedMonthlyAmount <= 0
  ) {
    return {
      requiredTransactions: null,
      transactionLimit,
      endDateTransactionLimit,
      effectiveTransactionLimit,
      isExceeded: false,
      reason: null,
    };
  }

  const requiredTransactions = Math.ceil(
    normalizedBalance / normalizedMonthlyAmount
  );
  const isExceeded =
    effectiveTransactionLimit !== null &&
    requiredTransactions > effectiveTransactionLimit;
  const isEndDateTheStricterLimit =
    endDateTransactionLimit !== null &&
    (transactionLimit === null || endDateTransactionLimit < transactionLimit);

  return {
    requiredTransactions,
    transactionLimit,
    endDateTransactionLimit,
    effectiveTransactionLimit,
    isExceeded,
    reason: isExceeded
      ? isEndDateTheStricterLimit
        ? "END_DATE_LIMIT_EXCEEDED"
        : "TRANSACTION_LIMIT_EXCEEDED"
      : null,
  };
};

export const getNonBudgetOfferTransactionLimitError = (
  offer: SelectedOfferLike | null | undefined,
  configuredLimit: unknown
): SelectedOfferLimitError | null => {
  // Budget Calculator already owns its transaction-limit assistance flow.
  if (!offer || offer.offer_type === "BC") return null;

  const transactionCount = Number(offer.no_of_transactions ?? 1);
  const transactionLimit = normalizeTransactionLimit(configuredLimit);

  if (!Number.isFinite(transactionCount) || transactionCount < 1) {
    return {
      code: "INVALID_TRANSACTION_COUNT",
      message: "This offer has an invalid number of transactions.",
    };
  }

  // Do not invent a client-side limit when the portal setting is absent or
  // malformed. The schedule API remains authoritative on submission.
  if (transactionLimit === null) return null;

  const normalizedCount = Math.ceil(transactionCount);
  const normalizedLimit = transactionLimit;

  if (normalizedCount <= normalizedLimit) return null;

  return {
    code: "TRANSACTION_LIMIT_EXCEEDED",
    message: `This offer requires ${normalizedCount} transactions, but the maximum allowed is ${normalizedLimit}.`,
  };
};

export const getSelectedOfferEndDateLimitError = ({
  offer,
  transactions = [],
  configuredLimit,
  now = new Date(),
}: {
  offer: SelectedOfferLike | null | undefined;
  transactions?: any[];
  configuredLimit: unknown;
  now?: Date;
}): SelectedOfferLimitError | null => {
  if (!offer) return null;

  const parsedLimit = parseEndDateLimit(configuredLimit);
  if (!parsedLimit) return null;

  const maximumDate = moment(now)
    .add(parsedLimit.value, parsedLimit.momentUnit)
    .endOf("day");
  const exceedsLimit = transactions.some((transaction) => {
    const dateValue = getScheduleDate(transaction);
    if (!dateValue) return false;

    const transactionDate = moment(
      dateValue,
      [
        "MM/DD/YYYY",
        "MM/DD/YYYY HH:mm:ss",
        "YYYY-MM-DD",
        "YYYY-MM-DD HH:mm:ss",
        moment.ISO_8601,
      ],
      true
    );

    return transactionDate.isValid() && transactionDate.isAfter(maximumDate);
  });

  if (!exceedsLimit) return null;

  return {
    code: "END_DATE_LIMIT_EXCEEDED",
    message: `This payment schedule exceeds the allowed end date of ${maximumDate.format("MM/DD/YYYY")}. Please choose another offer or adjust your budget`,
  };
};
